import { useCallback, useState } from 'react';
import {
  Stack,
  Inline,
  InlineItem,
  Text,
  Strong,
  UtilityButton,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuSeparator,
  ConfirmationDialog,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import type {
  AccessControl,
  Access,
  UserIdentity,
  OrganisationIdentity,
  ServiceAccountIdentity,
  Identity,
} from '@/types';
import {
  userAvatar,
  organisationAvatar,
  serviceAccountAvatar,
} from './identity-avatar';

const identityTypeName = (identity: Identity) => {
  switch (identity.type) {
    case 'organisation':
      return 'organisation';
    case 'user':
      return 'user';
    case 'service-account':
      return 'service account';
  }
};

const lastEditorTitle = (isMe: boolean, identity: Identity) => {
  if (isMe) {
    return 'You are the only person that can edit this inventory';
  } else {
    return `“${identity.name}” is the only ${identityTypeName(identity)} that can edit this inventory`;
  }
};
const lastEditorDescription = (isMe: boolean) => {
  return `To remove ${
    isMe ? 'your' : 'their'
  } access, make sure another organisation or person can edit this inventory.`;
};

const userDetail = (v: UserIdentity, isMe: boolean) => (
  <Stack space='none'>
    <Text>
      <Strong>
        {v.name}
        {isMe ? ' (You)' : ''}
      </Strong>
    </Text>
    <Text tone='secondary'>{v.email}</Text>
  </Stack>
);

const organisationDetail = (v: OrganisationIdentity) => (
  <Stack space='none'>
    <Text>
      <Strong>{v.name}</Strong>
    </Text>
    <Text tone='secondary'>
      {v.members === 1 ? `1 member` : `${v.members} members`}
    </Text>
  </Stack>
);

const serviceAccountDetail = (v: ServiceAccountIdentity) => (
  <Stack space='none'>
    <Text>
      <Strong>{v.name}</Strong>
    </Text>
    <Text tone='secondary'>Service account</Text>
  </Stack>
);

const AccessControlItem = (props: AccessControlItemProps) => {
  const {
    isMe,
    isEditor,
    isLastEditor,
    access,
    onRemove = noop,
    onChange = noop,
  } = props;
  const [lastEditorDialog, setLastEditorDialog] = useState(false);
  const openLastEditorDialog = useCallback(
    () => setLastEditorDialog(true),
    [setLastEditorDialog],
  );
  const closeLastEditorDialog = useCallback(
    () => setLastEditorDialog(false),
    [setLastEditorDialog],
  );

  const [confirmRemoveDialog, setConfirmRemoveDialog] = useState(false);
  const openConfirmRemoveDialog = useCallback(
    () => setConfirmRemoveDialog(true),
    [setConfirmRemoveDialog],
  );
  const closeConfirmRemoveDialog = useCallback(
    () => setConfirmRemoveDialog(false),
    [setConfirmRemoveDialog],
  );

  const onSetEditor = useCallback(() => {
    onChange(access.identity, 'editor');
  }, [onChange, access]);

  const onSetViewer = useCallback(() => {
    if (isLastEditor) {
      openLastEditorDialog();
    } else {
      onChange(access.identity, 'viewer');
    }
  }, [onChange, access, isLastEditor, openLastEditorDialog]);

  const onRemoveAccess = useCallback(() => {
    if (isLastEditor) {
      openLastEditorDialog();
    } else {
      openConfirmRemoveDialog();
    }
  }, [openConfirmRemoveDialog, openLastEditorDialog, isLastEditor]);

  const onRemoveAccessConfirm = useCallback(() => {
    onRemove(access.identity);
  }, [onRemove, access]);

  return (
    <>
      <Inline>
        <InlineItem>
          {access.identity.type === 'organisation' &&
            organisationAvatar(access.identity)}
          {access.identity.type === 'user' && userAvatar(access.identity)}
          {access.identity.type === 'service-account' &&
            serviceAccountAvatar(access.identity)}
        </InlineItem>
        <InlineItem sizing='fill-container'>
          {access.identity.type === 'organisation' &&
            organisationDetail(access.identity)}
          {access.identity.type === 'user' && userDetail(access.identity, isMe)}
          {access.identity.type === 'service-account' &&
            serviceAccountDetail(access.identity)}
        </InlineItem>
        <InlineItem>
          <DropdownMenu justify='end'>
            <DropdownMenuButton
              as={UtilityButton}
              dropdown
              inline
              magnitude='xsmall'
              disabled={!isEditor}
            >
              <span>
                {access.access === 'editor' ? 'Can edit' : 'Can view'}
              </span>
            </DropdownMenuButton>
            <DropdownMenuList>
              <DropdownMenuItem
                onSelect={onSetEditor}
                variant={access.access === 'editor' ? 'accent' : undefined}
              >
                Can edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={onSetViewer}
                variant={access.access === 'viewer' ? 'accent' : undefined}
              >
                Can view
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onRemoveAccess} variant='danger'>
                Remove access
              </DropdownMenuItem>
            </DropdownMenuList>
          </DropdownMenu>
        </InlineItem>
      </Inline>
      {confirmRemoveDialog && (
        <ConfirmationDialog
          cancelText='Don’t remove'
          confirmText='Remove access'
          icon='question'
          title={
            isMe
              ? `Remove your access to inventory?`
              : `Remove access for “${access.identity.name}”?`
          }
          onCancel={closeConfirmRemoveDialog}
          onConfirm={onRemoveAccessConfirm}
          variant='danger'
          minWidth={320}
        >
          {isMe ? 'You may no longer be able to access this workspace.' : null}
          {!isMe && access.identity.type === 'organisation'
            ? 'The people in this organisation may no longer be able to access this inventory.'
            : null}
          {!isMe && access.identity.type === 'user'
            ? 'This person may no longer be able to access this inventory.'
            : null}
          {!isMe && access.identity.type === 'service-account'
            ? 'This service account may no longer be able to access this inventory.'
            : null}
        </ConfirmationDialog>
      )}
      {lastEditorDialog && (
        <ConfirmationDialog
          confirmText='OK'
          icon='exclamation'
          minWidth={320}
          variant='primary'
          onCancel={closeLastEditorDialog}
          onConfirm={closeLastEditorDialog}
          showCancelButton={false}
          title={lastEditorTitle(isMe, access.identity)}
        >
          {lastEditorDescription(isMe)}
        </ConfirmationDialog>
      )}
    </>
  );
};

export type AccessControlItemProps = {
  isEditor: boolean;
  isMe: boolean;
  isLastEditor: boolean;
  onRemove?: (i: Identity) => void;
  onChange?: (i: Identity, v: Access) => void;
  access: AccessControl;
};

export { AccessControlItem };
